// C:\Users\cornn\source\repos\purehealthnexus\src\components\AiDashboard\charts\PieChart.jsx
import React, { useMemo, useRef, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import './PieChart.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const hexToRgb = (hex) => {
  const hexWithoutHash = hex.replace('#', '');
  return {
    r: parseInt(hexWithoutHash.substring(0, 2), 16),
    g: parseInt(hexWithoutHash.substring(2, 4), 16),
    b: parseInt(hexWithoutHash.substring(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) =>
  '#' +
  [r, g, b]
    .map((value) => {
      const clamped = Math.max(0, Math.min(255, Math.round(value)));
      const hexString = clamped.toString(16);
      return hexString.length === 1 ? '0' + hexString : hexString;
    })
    .join('');

const mixHex = (firstHex, secondHex, percentage) => {
  const firstColor = hexToRgb(firstHex);
  const secondColor = hexToRgb(secondHex);
  return rgbToHex({
    r: firstColor.r * (1 - percentage) + secondColor.r * percentage,
    g: firstColor.g * (1 - percentage) + secondColor.g * percentage,
    b: firstColor.b * (1 - percentage) + secondColor.b * percentage,
  });
};

const TINT_STEPS = [0.0, 0.06, 0.12, 0.18, 0.24];

/**
 * Builds a human-readable status label from a status key.
 */
function formatStatusLabel(statusKey) {
  if (typeof statusKey !== 'string' || statusKey.trim().length === 0) {
    return '';
  }

  const trimmed = statusKey.trim();

  if (trimmed === 'low') return 'Low';
  if (trimmed === 'high') return 'High';
  if (trimmed === 'on-goal' || trimmed === 'on-track') return 'On goal';
  if (trimmed === 'data-needed') return 'Data needed';

  // Generic fallback: capitalise first letter.
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * External HTML tooltip renderer for Chart.js.
 * Uses the values from tooltip callbacks (title + label) and renders them into
 * the existing `.chartjs-tooltip` container.
 */
function externalTooltipHandler(context) {
  const { chart, tooltip } = context;
  const canvasParent = chart.canvas.parentNode;

  let tooltipElement = canvasParent.querySelector('.chartjs-tooltip');

  if (!tooltipElement) {
    tooltipElement = document.createElement('div');
    tooltipElement.className = 'chartjs-tooltip';
    tooltipElement.innerHTML = `
      <div class="chartjs-tooltip__title"></div>
      <div class="chartjs-tooltip__body"></div>
    `;
    canvasParent.appendChild(tooltipElement);
  }

  // Hide tooltip if not active.
  if (tooltip.opacity === 0) {
    tooltipElement.style.opacity = 0;
    return;
  }

  const titleLines = tooltip.title || [];
  const bodyLines =
    tooltip.body?.flatMap((bodyItem) => bodyItem.lines || []) || [];

  const titleElement = tooltipElement.querySelector('.chartjs-tooltip__title');
  const bodyElement = tooltipElement.querySelector('.chartjs-tooltip__body');

  titleElement.textContent = titleLines.join('\n');

  if (bodyLines.length > 0) {
    // Join multiple label lines with a line break if needed.
    bodyElement.innerHTML = bodyLines.join('<br/>');
    bodyElement.style.display = 'block';
  } else {
    bodyElement.textContent = '';
    bodyElement.style.display = 'none';
  }

  const { offsetLeft: canvasX, offsetTop: canvasY } = chart.canvas;
  tooltipElement.style.opacity = 1;
  tooltipElement.style.left = `${canvasX + tooltip.caretX}px`;
  tooltipElement.style.top = `${canvasY + tooltip.caretY}px`;
  tooltipElement.style.pointerEvents = 'none';
}

export default function TwoRingDonutChart({ view, onViewChange, chartData }) {
  const chartRef = useRef(null);

  const LOW_COLOR = '#FF9F1A';
  const ON_GOAL_COLOR = '#22C55E';
  const HIGH_COLOR = '#EF4444';

  const currentData = useMemo(() => {
    if (!chartData) return null;
    if (view === 'class') return chartData.vitamins;
    if (view === 'survival') return chartData.minerals;
    if (view === 'fats') return chartData.fats;
    return null;
  }, [chartData, view]);

  const processedData = useMemo(() => {
    if (!currentData) return { innerData: [], outerData: [], summary: null };

    const { low, onGoal, high, categoryLabel } = currentData;

    const tintColor = (baseColor, index) =>
      mixHex(baseColor, '#ffffff', TINT_STEPS[index % TINT_STEPS.length]);

    /**
     * Helper to copy over any useful numeric fields onto a segment, so we can
     * use them later inside tooltip callbacks without touching the data source.
     */
    const buildSegmentMeta = (nutrient, status) => {
      const hasDailyValue =
        typeof nutrient.dv === 'number' && Number.isFinite(nutrient.dv);
      const hasEnergyPercent =
        typeof nutrient.ePct === 'number' && Number.isFinite(nutrient.ePct);

      return {
        nutrientId: nutrient.id,
        label: nutrient.label,
        status,
        dvPercent: hasDailyValue ? nutrient.dv : null,
        energyPercent: hasEnergyPercent ? nutrient.ePct : null,
      };
    };

    let innerData = [];
    if (low.length + high.length > 0) {
      innerData = [
        ...low.map((nutrient, index) => ({
          label: nutrient.label,
          value: 1,
          backgroundColor: tintColor(LOW_COLOR, index),
          ...buildSegmentMeta(nutrient, 'low'),
        })),
        ...high.map((nutrient, index) => ({
          label: nutrient.label,
          value: 1,
          backgroundColor: tintColor(HIGH_COLOR, index),
          ...buildSegmentMeta(nutrient, 'high'),
        })),
      ];
    } else if (onGoal.length > 0) {
      // Single inner ring when everything is on goal.
      innerData = [
        {
          label: `All On Goal (${onGoal.length})`,
          value: onGoal.length,
          backgroundColor: ON_GOAL_COLOR,
          nutrientId: null,
          status: 'on-goal',
          dvPercent: null,
          energyPercent: null,
        },
      ];
    }

    const makeItems = (sourceArray, baseColor, status) =>
      sourceArray.map((nutrient, index) => ({
        label: nutrient.label,
        value: 1,
        backgroundColor: tintColor(baseColor, index),
        ...buildSegmentMeta(nutrient, status),
      }));

    const outerData = [
      ...makeItems(low, LOW_COLOR, 'low'),
      ...makeItems(high, HIGH_COLOR, 'high'),
      ...makeItems(onGoal, ON_GOAL_COLOR, 'on-goal'),
    ];

    const summary = {
      low: low.length,
      onGoal: onGoal.length,
      high: high.length,
      total: low.length + onGoal.length + high.length,
      categoryLabel,
    };

    return { innerData, outerData, summary };
  }, [currentData]);

  const { innerData, outerData, summary } = processedData;

  const originalColors = useRef({
    inner: innerData.map((segment) => segment.backgroundColor),
    outer: outerData.map((segment) => segment.backgroundColor),
  });

  useEffect(() => {
    originalColors.current = {
      inner: innerData.map((segment) => segment.backgroundColor),
      outer: outerData.map((segment) => segment.backgroundColor),
    };
  }, [innerData, outerData]);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, [summary]);

  /**
   * Build the Chart.js data object, including a `segmentMeta` array on each
   * dataset so tooltip callbacks can look up dv%, E% and status per slice.
   */
  const chartJsData = useMemo(
    () => ({
      labels: [
        ...innerData.map((segment) => segment.label),
        ...outerData.map((segment) => segment.label),
      ],
      datasets: [
        {
          label: summary?.categoryLabel
            ? `${summary.categoryLabel} Status`
            : 'Status Summary',
          data: innerData.map((segment) => segment.value),
          backgroundColor: innerData.map((segment) => segment.backgroundColor),
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverBorderWidth: 3,
          hoverBorderColor: '#ffffff',
          spacing: 2,
          // Custom metadata for our tooltip logic:
          segmentMeta: innerData,
        },
        {
          label: '',
          data: outerData.map((segment) => segment.value),
          backgroundColor: outerData.map((segment) => segment.backgroundColor),
          borderWidth: 1,
          borderColor: '#ffffff',
          hoverBorderWidth: 2,
          hoverBorderColor: '#ffffff',
          spacing: 1,
          // Custom metadata for our tooltip logic:
          segmentMeta: outerData,
        },
      ],
    }),
    [innerData, outerData, summary],
  );

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: '55%',
    radius: '92%',
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false, // We render via the external HTML tooltip.
        external: externalTooltipHandler,
        callbacks: {
          /**
           * Tooltip title: just the nutrient label (or the aggregated label).
           */
          title: (tooltipItems) => {
            const firstItem = tooltipItems?.[0];
            if (!firstItem) return '';

            const chartInstance = firstItem.chart;
            const dataset =
              chartInstance.data.datasets[firstItem.datasetIndex] || {};
            const segmentMeta = dataset.segmentMeta || [];
            const metaEntry = segmentMeta[firstItem.dataIndex];

            if (metaEntry && typeof metaEntry.label === 'string') {
              return metaEntry.label;
            }

            return firstItem.label || '';
          },

          /**
           * Tooltip body: "current / goal" style message derived from
           * dv% or energy % plus the nutrient's status.
           */
          label: (tooltipItem) => {
            const chartInstance = tooltipItem.chart;
            const dataset =
              chartInstance.data.datasets[tooltipItem.datasetIndex] || {};
            const segmentMeta = dataset.segmentMeta || [];
            const metaEntry = segmentMeta[tooltipItem.dataIndex];

            if (!metaEntry) {
              return '';
            }

            const { dvPercent, energyPercent, status } = metaEntry;
            const statusLabel = formatStatusLabel(status);

            // Daily value based nutrients (vitamins, minerals).
            if (typeof dvPercent === 'number' && Number.isFinite(dvPercent)) {
              const roundedPercent = Math.round(dvPercent);
              const statusSuffix = statusLabel
                ? ` • ${statusLabel}`
                : '';
              // "current/goal" expressed in %DV vs 100% target.
              return `Current: ${roundedPercent}% ≈ 100%${statusSuffix}`;
            }

            // Energy percentage based nutrients (fats, etc.).
            if (
              typeof energyPercent === 'number' &&
              Number.isFinite(energyPercent)
            ) {
              const roundedEnergyPercent = Math.round(energyPercent);
              const statusSuffix = statusLabel
                ? ` • ${statusLabel}`
                : '';
              return `Current: ${roundedEnergyPercent}% of energy${statusSuffix}`;
            }

            // Fallback to just the status if no numeric value is available.
            return statusLabel || '';
          },
        },
      },
    },
    interaction: { mode: 'point', intersect: true },
    onHover: (_event, activeElements, chartInstance) => {
      if (activeElements.length > 0) {
        chartInstance.data.datasets.forEach((dataset, datasetIndex) => {
          const originals =
            datasetIndex === 0
              ? originalColors.current.inner
              : originalColors.current.outer;

          dataset.backgroundColor = originals.map((color, colorIndex) => {
            const isActive = activeElements.some(
              (element) =>
                element.datasetIndex === datasetIndex &&
                element.index === colorIndex,
            );

            if (isActive) {
              return color;
            }

            if (typeof color === 'string' && color.startsWith('#')) {
              return mixHex(color, '#e5e7eb', 0.45);
            }

            return color;
          });
        });
        chartInstance.update('none');
      } else {
        chartInstance.data.datasets[0].backgroundColor =
          originalColors.current.inner;
        chartInstance.data.datasets[1].backgroundColor =
          originalColors.current.outer;
        chartInstance.update('none');
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 800,
      easing: 'easeInOutQuart',
    },
  };

  const summaryRef = useRef(summary);
  summaryRef.current = summary;

  const centerTextPlugin = useMemo(
    () => ({
      id: 'centerText',
      afterDraw: (chartInstance) => {
        const { ctx, chartArea } = chartInstance;
        if (!chartArea) return;
        const { left, top, width, height } = chartArea;

        ctx.save();
        const text = summaryRef.current?.categoryLabel || '';
        ctx.font =
          '600 26px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif';
        ctx.fillStyle = '#2D3748';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, left + width / 2, top + height / 2);
        ctx.restore();
      },
    }),
    [],
  );

  if (!innerData.length || !outerData.length) {
    return (
      <div className="two-ring-chart-container">
        <div
          className="chart-toggle-wrapper"
          role="tablist"
          aria-label="Nutrient view"
        >
          <div className="chart-toggle-group">
            <button
              onClick={() => onViewChange('class')}
              className={`chart-toggle-btn ${
                view === 'class' ? 'active' : ''
              }`}
            >
              Vitamins
            </button>
            <button
              onClick={() => onViewChange('survival')}
              className={`chart-toggle-btn ${
                view === 'survival' ? 'active' : ''
              }`}
            >
              Minerals
            </button>
            <button
              onClick={() => onViewChange('fats')}
              className={`chart-toggle-btn ${
                view === 'fats' ? 'active' : ''
              }`}
            >
              Fats
            </button>
          </div>
        </div>
        <div
          className="chart-canvas-wrapper"
          style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}
        >
          No {summary?.categoryLabel || 'nutrient'} data available
        </div>
      </div>
    );
  }

  return (
    <div className="two-ring-chart-container">
      <div
        className="chart-toggle-wrapper"
        role="tablist"
        aria-label="Nutrient view"
      >
        <div className="chart-toggle-group">
          <button
            onClick={() => onViewChange('class')}
            className={`chart-toggle-btn ${
              view === 'class' ? 'active' : ''
            }`}
            role="tab"
            id="tab-class"
            aria-selected={view === 'class'}
            aria-controls="panel-chart"
            tabIndex={view === 'class' ? 0 : -1}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') onViewChange('survival');
            }}
          >
            Vitamins
          </button>
          <button
            onClick={() => onViewChange('survival')}
            className={`chart-toggle-btn ${
              view === 'survival' ? 'active' : ''
            }`}
            role="tab"
            id="tab-survival"
            aria-selected={view === 'survival'}
            aria-controls="panel-chart"
            tabIndex={view === 'survival' ? 0 : -1}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') onViewChange('class');
              if (event.key === 'ArrowRight') onViewChange('fats');
            }}
          >
            Minerals
          </button>
          <button
            onClick={() => onViewChange('fats')}
            className={`chart-toggle-btn ${
              view === 'fats' ? 'active' : ''
            }`}
            role="tab"
            id="tab-fats"
            aria-selected={view === 'fats'}
            aria-controls="panel-chart"
            tabIndex={view === 'fats' ? 0 : -1}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') onViewChange('survival');
            }}
          >
            Fats
          </button>
        </div>
      </div>

      <div
        className="chart-canvas-wrapper"
        role="tabpanel"
        id="panel-chart"
        aria-labelledby={
          view === 'class'
            ? 'tab-class'
            : view === 'survival'
            ? 'tab-survival'
            : 'tab-fats'
        }
      >
        <Doughnut
          ref={chartRef}
          data={chartJsData}
          options={options}
          plugins={[centerTextPlugin]}
        />
      </div>

      <div className="chart-legend">
        <p className="chart-legend-text">
          {summary && (
            <>
              {summary.low > 0 && `${summary.low} Low`}
              {summary.low > 0 && summary.high > 0 && ', '}
              {summary.high > 0 && `${summary.high} High`}
              {summary.low === 0 &&
                summary.high === 0 &&
                `All ${summary.onGoal} On Goal`}
              {' • '}
              {summary.onGoal} On Goal
            </>
          )}
        </p>
      </div>
    </div>
  );
}
